import React, { useState, useEffect } from "react";
import { TrendingUp, Coins, Video, Gamepad2, Pickaxe } from "lucide-react";
import useThemeHook from "../hooks/useThemeHook";
import { useSelector } from "react-redux";
import { UserModelData } from "../../types";

interface EarningsEntry {
  source: string;
  amount: number;
  icon: string;
}

function getTodayKey(userId: string): string {
  const today = new Date().toISOString().split("T")[0];
  return `todayEarnings_${userId}_${today}`;
}

function getTodaysEarnings(userId: string): number {
  try {
    const raw = localStorage.getItem(getTodayKey(userId));
    return raw ? parseFloat(raw) : 0;
  } catch {
    return 0;
  }
}

export function addTodaysEarnings(userId: string, amount: number) {
  const current = getTodaysEarnings(userId);
  localStorage.setItem(getTodayKey(userId), (current + amount).toFixed(2));
}

const TodaysEarnings: React.FC = () => {
  const theme = useThemeHook();
  const isDark = theme === "dark";
  const userData: UserModelData = useSelector((state: any) => state?.actualUser);
  const [earnings, setEarnings] = useState(0);

  useEffect(() => {
    if (userData?.user?.id) {
      setEarnings(getTodaysEarnings(userData.user.id));
    }

    // Poll for updates
    const interval = setInterval(() => {
      if (userData?.user?.id) {
        setEarnings(getTodaysEarnings(userData.user.id));
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [userData?.user?.id]);

  return (
    <div className={`rounded-2xl border p-4 ${
      isDark ? "bg-slate-800/50 border-slate-700/50" : "bg-white border-gray-200 shadow-sm"
    }`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className={`p-2 rounded-xl ${isDark ? "bg-emerald-900/30" : "bg-emerald-50"}`}>
            <TrendingUp size={18} className="text-emerald-500" />
          </div>
          <div>
            <p className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? "text-slate-500" : "text-gray-400"}`}>
              Today's Earnings
            </p>
            <p className={`text-xl font-black ${isDark ? "text-white" : "text-gray-900"}`}>
              +{earnings.toFixed(2)} <span className={`text-sm ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>ATH</span>
            </p>
          </div>
        </div>

        {/* Mini sources */}
        <div className="flex items-center gap-2">
          <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-bold ${
            isDark ? "bg-slate-700 text-slate-400" : "bg-gray-100 text-gray-500"
          }`}>
            <Pickaxe size={10} />
            <span>Grants</span>
          </div>
          <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-bold ${
            isDark ? "bg-slate-700 text-slate-400" : "bg-gray-100 text-gray-500"
          }`}>
            <Video size={10} />
            <span>Ads</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TodaysEarnings;
